import { useState, useEffect, useMemo } from 'react'
import toast from 'react-hot-toast'
import { useGetClubSettings, useUpdateClubSettings } from '@/services/club'
import { Spinner, EmptyState, CMButton } from './ui'

interface ClubSettingsTabProps {
  clubId: number
}

export const ClubSettingsTab = ({ clubId }: ClubSettingsTabProps) => {
  const { data, isLoading, error, refetch } = useGetClubSettings(clubId)
  const { mutate: updateSettings, isLoading: isSaving } = useUpdateClubSettings()

  const [isEditing, setIsEditing] = useState(false)
  const [editedSettings, setEditedSettings] = useState<Record<string, any>>({})

  const settings = data?.result?.settings ?? {}
  const metadata = data?.result?.settingsMetadata ?? {}

  useEffect(() => {
    if (settings && Object.keys(settings).length > 0) {
      setEditedSettings(settings)
    }
  }, [data])

  const { editableKeys, readOnlyKeys } = useMemo(() => {
    const all = Object.keys(settings)
    return {
      editableKeys: all.filter((k) => metadata[k]?.editable !== false),
      readOnlyKeys: all.filter((k) => metadata[k]?.editable === false),
    }
  }, [settings, metadata])

  const handleSave = () => {
    const editableOnly = Object.fromEntries(editableKeys.map((k) => [k, editedSettings[k]]))
    updateSettings(
      { clubId, payload: { settings: editableOnly } },
      {
        onSuccess: () => {
          toast.success('Settings saved successfully')
          setIsEditing(false)
          refetch()
        },
        onError: () => {
          toast.error('Failed to save settings')
        },
      }
    )
  }

  const handleCancel = () => {
    setEditedSettings(settings)
    setIsEditing(false)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    )
  }

  if (error) {
    return (
      <EmptyState
        message="Failed to load settings"
        action={{ label: 'Retry', onClick: () => refetch() }}
        className="py-8"
      />
    )
  }

  if (editableKeys.length === 0 && readOnlyKeys.length === 0) {
    return (
      <EmptyState
        icon={
          <svg className="w-10 h-10 text-gray-300 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        }
        message="No settings available"
        className="py-8"
      />
    )
  }

  const renderSettingRow = (key: string, editable: boolean) => {
    const value = isEditing && editable ? editedSettings[key] : settings[key]
    const meta = metadata[key]
    const isBoolean = typeof settings[key] === 'boolean'
    const isNumber = typeof settings[key] === 'number'
    const displayKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())

    return (
      <div key={key} className="py-2 flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <span className="text-sm font-medium text-gray-900">{displayKey}</span>
          {meta?.description && (
            <p className="text-[11px] text-gray-500 mt-0.5">{meta.description}</p>
          )}
        </div>

        <div className="flex-shrink-0">
          {isEditing && editable ? (
            isBoolean ? (
              <button
                onClick={() =>
                  setEditedSettings((prev) => ({ ...prev, [key]: !prev[key] }))
                }
                className={`relative inline-flex h-5 w-9 items-center transition-colors ${
                  value ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform bg-white transition-transform ${
                    value ? 'translate-x-[18px]' : 'translate-x-0.5'
                  }`}
                />
              </button>
            ) : isNumber ? (
              <input
                type="number"
                value={value}
                onChange={(e) =>
                  setEditedSettings((prev) => ({ ...prev, [key]: Number(e.target.value) }))
                }
                className="w-20 px-2 py-1 border border-gray-200 text-xs text-gray-900 text-right focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
            ) : (
              <input
                type="text"
                value={value ?? ''}
                onChange={(e) =>
                  setEditedSettings((prev) => ({ ...prev, [key]: e.target.value }))
                }
                className="w-40 px-2 py-1 border border-gray-200 text-xs text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
            )
          ) : (
            <span className={`text-sm ${editable ? 'text-gray-900' : 'text-gray-400'}`}>
              {isBoolean ? (value ? 'Enabled' : 'Disabled') : String(value ?? '-')}
            </span>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Editable Settings */}
      {editableKeys.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Club Settings</h4>
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <CMButton variant="muted" size="sm" onClick={handleCancel} className="text-xs py-1">Cancel</CMButton>
                  <CMButton variant="primary" size="sm" onClick={handleSave} disabled={isSaving} className="text-xs py-1">
                    {isSaving ? 'Saving...' : 'Save'}
                  </CMButton>
                </>
              ) : (
                <CMButton variant="ghost" size="sm" onClick={() => setIsEditing(true)} className="text-xs py-1">
                  Edit
                </CMButton>
              )}
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {editableKeys.map((key) => renderSettingRow(key, true))}
          </div>
        </div>
      )}

      {/* Read-only Settings */}
      {readOnlyKeys.length > 0 && (
        <div className={editableKeys.length > 0 ? 'pt-3 border-t border-gray-200' : ''}>
          <div className="flex items-center gap-2 mb-2">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">DUPR Managed</h4>
            <span className="px-1.5 py-0.5 text-[9px] font-medium bg-gray-100 text-gray-500">Read-only</span>
          </div>
          <div className="divide-y divide-gray-100 opacity-75">
            {readOnlyKeys.map((key) => renderSettingRow(key, false))}
          </div>
        </div>
      )}
    </div>
  )
}
